import moment from 'moment';

/**
 * Medical approval email state for the booking page, from either API response:
 * - the "Approved" toggle (`participant/medicalDocument/statusChange`): emailSendAt, emailAlreadySentAt,
 *   noGuardianEmail, serverNow;
 * - the booking detail (`bookingCamperDetail`): medicalApprovalEmailSendAt, medicalApprovalEmailSentAt,
 *   serverNow.
 * Times are ms. `clockOffset` (server minus browser clock) keeps the countdown right when the
 * director's computer clock is off.
 */
export const medicalEmailStateFrom = (data = {}) => ({
    sendAt: data.emailSendAt ?? data.medicalApprovalEmailSendAt ?? null,
    sentAt: data.emailAlreadySentAt ?? data.medicalApprovalEmailSentAt ?? null,
    noGuardianEmail: !!data.noGuardianEmail,
    clockOffset: data.serverNow ? data.serverNow - Date.now() : 0,
});

export const formatEmailSentAt = sentAt => moment(sentAt).format('MMM D, h:mm A');

// Time left as m:ss, rounded up so the last second shows 0:01 rather than 0:00.
export const formatCountdown = ms => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

// Email delay in words: under a minute shows seconds (0.5 -> "30 seconds").
export const formatEmailDelay = minutes => {
    if (minutes < 1) {
        const seconds = Math.round(minutes * 60);
        return `${seconds} second${seconds === 1 ? '' : 's'}`;
    }
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

// Toast after the "Approved" toggle; null keeps the existing messages.
export const medicalApprovalToast = (data = {}) => {
    const status = data.result?.medicalDocumentStatus;
    if (status === 1) {
        if (data.emailScheduled) {
            return `Medical clearance approved. The parent will be emailed in about ${formatEmailDelay(data.emailDelayMinutes)}. Switch off to cancel.`;
        }
        if (data.emailAlreadySentAt) {
            return `Medical clearance approved. The parent was already emailed on ${formatEmailSentAt(data.emailAlreadySentAt)}.`;
        }
        if (data.noGuardianEmail) {
            return 'Medical clearance approved. No parent email on file, so no email will be sent.';
        }
    }
    if (status === 2 && data.emailCancelled) {
        return 'Approval removed. The pending email was cancelled.';
    }
    return null;
};
